import classNames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { FC } from 'react';
import { z } from 'zod';

import type { BlogType, MediaDataStripe } from '@/services/strapi/blogs';
import { documentDateFormatter } from '@/utils/documentDateFormatter';

import Chip from '../Chip';
import Block from '../ResumeChecker/Block';
import Text from '../Text';
import { blogTypesConfig } from './blogTypesConfig';

export type PostCardProps = {
  title: string;
  img: MediaDataStripe;
  tag?: string;
  slug: string;
  createdAt: string;
  className?: string;
  blogType: BlogType;
};

const PostCard: FC<PostCardProps> = ({
  createdAt,
  img,
  slug,
  tag,
  title,
  className,
  blogType,
}) => {
  const { query } = useRouter();
  const search = z.string().optional().parse(query.search);

  const { basePath, chipVariant } = blogTypesConfig[blogType];

  return (
    <Link
      href={{ pathname: `${basePath}/[slug]`, query: { slug, search } }}
      className={classNames('w-full', className)}
    >
      <Block className="h-full min-h-max !pb-10 shadow-big-moduls transition-shadow hover:shadow-jb-neutral-600 lg:!p-6">
        <Image
          width={img.width}
          height={img.height}
          src={img.url}
          placeholder={img.placeholder ? 'blur' : 'empty'}
          blurDataURL={img.placeholder ?? undefined}
          alt={title}
          className="h-60 w-full overflow-hidden rounded-xl object-cover object-center"
        />
        <div className="mt-4 flex flex-col gap-5">
          {!!tag && (
            <Chip
              className="flex w-fit !gap-0 font-semibold"
              size="sm-long"
              variant={chipVariant}
            >
              <span className="italic">#</span>
              {tag}
            </Chip>
          )}
          <Text variant="h4" className="font-semibold">
            {title}
          </Text>
          <Text className="font-semibold text-jb-neutral-700">
            {documentDateFormatter(createdAt)}
          </Text>
        </div>
      </Block>
    </Link>
  );
};

export default PostCard;
